import React, { useCallback, useEffect, useMemo, useState, useRef, Fragment, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronDown, Minimize2, Maximize2, Fullscreen, Shrink, ArrowUpDown, ArrowUp, ArrowDown, TrendingUp, TrendingDown, Minus, Download, HardHat, ShieldAlert, MapPin, SlidersHorizontal, Search, UsersIcon, Building2, X, AlertTriangle, Sparkles, ChevronUp, Zap, ExternalLink, Wrench } from 'lucide-react';
import { EnrichedRiskArea } from '../../types/prevention';
import { ENRICHED_MOCK_RISK_AREAS } from '../../data/mockPreventionData';
import { RiskFactorScores, FactorSeverity, FACTOR_CONFIG, RISK_LEVEL_CONFIG, getRiskBand } from '../RiskAreasWidget';
import { Toast } from '../Toast';
import { RiskTuningPanel } from './RiskTuningPanel';
import { ModelCustomizationModal } from './ModelCustomizationModal';
import { REGIONS, getRegionForProject } from '../../data/regionData';
import { getDriverExplanation } from '../../data/mockRiskDriverExplanations';
import { RecommendationDetailModal } from './RecommendationDetailModal';
import { Recommendation, MOCK_RECOMMENDATIONS } from '../../data/mockRecommendationsData';
// ─── UTILITY: Score to Risk Level ────────────────────────────────────────────
const scoreToRiskLevel = (s: number): 'critical' | 'high' | 'medium' | 'low' => s >= 85 ? 'critical' : s >= 70 ? 'high' : s >= 50 ? 'medium' : 'low';
// ─── PARAMETER & CATEGORY CONFIG ────────────────────────────────────────────
export interface ParamDef {
  id: string;
  label: string;
  weight: number;
  dataAvailable: boolean;
  mapFromFactor?: keyof RiskFactorScores | string;
  pendingReason?: string;
  aiEvaluated?: boolean;
  tooltip?: string;
}
export interface CategoryDef {
  id: string;
  label: string;
  icon: string;
  params: ParamDef[];
  tooltip?: string;
}
export const DEFAULT_CATEGORIES: CategoryDef[] = [{
  id: 'planningControls',
  label: 'Safety Planning & Controls',
  icon: '📝',
  tooltip: 'Pre-work safety planning quality and verification that controls are in place. Includes PTP reviews, JHA, toolbox talks, permits, PPE, and safety control validation. Click to expand parameters.',
  params: [{
    id: 'ptpQuality',
    label: 'PTP Quality',
    weight: 10,
    dataAvailable: true,
    mapFromFactor: 'ptpQuality',
    aiEvaluated: true,
    tooltip: 'AI-evaluated quality of Pre-Task Plans — completeness, specificity, hazard identification accuracy. Also factors in Permit Coverage & Quality (are required permits obtained, current, and properly scoped for the work).'
  }, {
    id: 'ptpEngagement',
    label: 'PTP Engagement',
    weight: 10,
    dataAvailable: true,
    mapFromFactor: 'ptpEngagement',
    tooltip: 'Crew participation and engagement during PTP reviews. E.g., attendance rate, sign-off completeness, time spent reviewing vs. rubber-stamping.'
  }, {
    id: 'jhaQuality',
    label: 'JHA Quality',
    weight: 5,
    dataAvailable: true,
    mapFromFactor: 'documents',
    aiEvaluated: true,
    tooltip: 'AI-evaluated quality of Job Hazard Analyses — specificity of hazards identified, appropriateness of controls, completeness relative to scope of work.'
  }, {
    id: 'toolboxEngagement',
    label: 'Toolbox Engagement',
    weight: 4,
    dataAvailable: true,
    mapFromFactor: 'ptpEngagement',
    tooltip: 'Quality and participation in toolbox talks. E.g., attendance rates, topic relevance, worker engagement level, frequency of talks per crew.'
  }, {
    id: 'controlsValidation',
    label: 'Controls Validation',
    weight: 6,
    dataAvailable: true,
    mapFromFactor: 'inspections',
    aiEvaluated: true,
    tooltip: 'AI-verified confirmation that planned safety controls are actually implemented in the field. E.g., fall protection anchors installed, barricades in place, lockout/tagout applied, ventilation running.'
  }, {
    id: 'ppeValidation',
    label: 'PPE Validation',
    weight: 4,
    dataAvailable: true,
    mapFromFactor: 'inspections',
    tooltip: 'Verification that required Personal Protective Equipment is worn correctly. E.g., hard hats, harnesses, safety glasses, high-vis vests, respiratory protection.'
  }, {
    id: 'sdsCompliance',
    label: 'SDS Compliance & Engagement',
    weight: 4,
    dataAvailable: true,
    mapFromFactor: 'documents',
    tooltip: 'Safety Data Sheet availability, currency, accessibility, and worker engagement with hazardous material protocols. E.g., SDS binder up to date, workers trained on relevant chemicals, crew awareness of SDS locations and procedures.'
  }]
}, {
  id: 'observationsInspections',
  label: 'Observations & Inspections',
  icon: '👁️',
  tooltip: 'Field detection activity — how well safety issues are being found and tracked through observations and formal inspections. Click to expand parameters.',
  params: [{
    id: 'observationFrequency',
    label: 'Observation Frequency',
    weight: 5,
    dataAvailable: true,
    mapFromFactor: 'inspections',
    tooltip: 'How often safety observations are conducted relative to site activity. E.g., daily safety walks, behavioral observations per crew, observation-to-worker ratio.'
  }, {
    id: 'observationSeverity',
    label: 'Observation Severity',
    weight: 4,
    dataAvailable: true,
    mapFromFactor: 'incidents',
    tooltip: 'Average severity of issues found during field observations. E.g., minor housekeeping vs. unprotected edges, unsafe lifting practices, missing fall protection.'
  }, {
    id: 'openObservationCount',
    label: 'Open Observation Count',
    weight: 4,
    dataAvailable: true,
    mapFromFactor: 'incidents',
    tooltip: 'Number of unresolved safety observations awaiting corrective action. High counts indicate follow-through gaps.'
  }, {
    id: 'positiveObservationRatio',
    label: 'Positive Observation Ratio',
    weight: 3,
    dataAvailable: true,
    mapFromFactor: 'inspections',
    tooltip: 'Ratio of positive (safe behavior) to negative (at-risk behavior) observations. Higher is better — indicates proactive safety culture.'
  }, {
    id: 'inspectionCompliance',
    label: 'Inspection Compliance',
    weight: 5,
    dataAvailable: true,
    mapFromFactor: 'inspections',
    tooltip: 'Percentage of required inspections completed on schedule. E.g., daily site inspections, weekly crane inspections, monthly fire extinguisher checks, equipment certifications.'
  }, {
    id: 'reinspectionRate',
    label: 'Reinspection Rate',
    weight: 3,
    dataAvailable: true,
    mapFromFactor: 'inspections',
    tooltip: 'How often areas require re-inspection due to unresolved findings. High rates suggest recurring issues or poor corrective action follow-through.'
  }]
}, {
  id: 'workforceContractors',
  label: 'Workforce & Contractors',
  icon: '👷',
  tooltip: 'People and partner risk — worker training, experience levels, fatigue, crew stability, supervision coverage, and subcontractor safety history. Click to expand parameters.',
  params: [{
    id: 'workerCompetency',
    label: 'Worker Competency',
    weight: 6,
    dataAvailable: true,
    mapFromFactor: 'workers',
    tooltip: 'Training completeness and certification status of the workforce. E.g., OSHA 30 completion, trade-specific certifications, site orientation, task-specific training.'
  }, {
    id: 'newWorkerRatio',
    label: 'New Worker Ratio',
    weight: 3,
    dataAvailable: true,
    mapFromFactor: 'workers',
    tooltip: 'Percentage of workers new to the site (< 2 weeks). New workers have higher incident rates due to unfamiliarity with site-specific hazards.'
  }, {
    id: 'fatigueRisk',
    label: 'Fatigue Risk',
    weight: 4,
    dataAvailable: true,
    mapFromFactor: 'workers',
    tooltip: 'Risk of worker fatigue based on hours worked, overtime patterns, and shift schedules. E.g., consecutive 12-hour days, night shifts, weekend work.'
  }, {
    id: 'crewStability',
    label: 'Crew Stability',
    weight: 2,
    dataAvailable: true,
    mapFromFactor: 'workers',
    tooltip: 'Consistency of crew composition over time. Frequent crew changes disrupt communication and increase risk. E.g., turnover rate, crew reassignment frequency.'
  }, {
    id: 'supervisionRatio',
    label: 'Supervision Ratio',
    weight: 4,
    dataAvailable: true,
    mapFromFactor: 'workers',
    tooltip: 'Ratio of supervisors/foremen to workers on site. Lower ratios mean less direct oversight, increasing risk of unsafe practices going unnoticed. E.g., 1:10 is typical, 1:20+ is elevated risk.'
  }, {
    id: 'contractorHistory',
    label: 'Contractors Records',
    weight: 5,
    dataAvailable: true,
    mapFromFactor: 'workers',
    tooltip: 'Historical safety performance of subcontractors. E.g., TRIR, LRIR, EMR from OSHA 300A logs, past project safety records, insurance claims history.'
  }, {
    id: 'contractorObservations',
    label: 'Contractors Observations & Inspections',
    weight: 3,
    dataAvailable: true,
    mapFromFactor: 'inspections',
    tooltip: 'Safety observation and inspection trends specific to each subcontractor. E.g., repeat violations by trade, observation severity by contractor, inspection findings, positive observation rates.'
  }]
}, {
  id: 'incidentsLoopClosure',
  label: 'Incidents & Loop Closure',
  icon: '⚠️',
  tooltip: 'Incident tracking and follow-through — open incidents, recurrence patterns, near-miss reporting, and corrective action closure rates. Click to expand parameters.',
  params: [{
    id: 'openIncidents',
    label: 'Open Incident Count',
    weight: 5,
    dataAvailable: true,
    mapFromFactor: 'incidents',
    tooltip: 'Number of unresolved safety incidents. E.g., open injury reports, pending near-miss investigations, unresolved property damage cases.'
  }, {
    id: 'incidentRecurrence',
    label: 'Incident Recurrence',
    weight: 4,
    dataAvailable: true,
    mapFromFactor: 'incidents',
    tooltip: 'Rate of similar incidents repeating. E.g., multiple slip/trip events in the same area, recurring struck-by incidents on the same trade.'
  }, {
    id: 'nearMissRatio',
    label: 'Near-Miss Ratio',
    weight: 3,
    dataAvailable: true,
    mapFromFactor: 'incidents',
    tooltip: 'Ratio of near-miss reports to actual incidents. Higher ratios indicate better reporting culture — teams catching hazards before they cause harm.'
  }, {
    id: 'correctiveActionClosure',
    label: 'Corrective Action Closure',
    weight: 3,
    dataAvailable: true,
    mapFromFactor: 'incidents',
    tooltip: 'Rate and timeliness of closing corrective actions from incidents. E.g., % of actions closed within SLA, average days to closure, overdue action count.'
  }]
}, {
  id: 'inherentRiskEnvironment',
  label: 'Inherent Risk & Environment',
  icon: '🌤',
  tooltip: 'Site conditions and inherent risk factors that exist regardless of safety processes — project type, construction phase, weather, congestion, hazardous materials, heavy equipment. Click to expand parameters.',
  params: [{
    id: 'projectType',
    label: 'Project Type',
    weight: 4,
    dataAvailable: true,
    mapFromFactor: 'weather',
    tooltip: 'Inherent risk level based on construction type. E.g., high-rise (higher fall risk), demolition (structural collapse), tunneling (confined space), renovation (lead/asbestos).'
  }, {
    id: 'projectPhase',
    label: 'Project Phase',
    weight: 4,
    dataAvailable: true,
    mapFromFactor: 'weather',
    tooltip: 'Current construction phase and its associated risk profile. E.g., excavation (cave-in), steel erection (falls, struck-by), concrete pour (formwork collapse), finishing (lower risk).'
  }, {
    id: 'weatherSeverity',
    label: 'Weather Severity',
    weight: 5,
    dataAvailable: true,
    mapFromFactor: 'weather',
    tooltip: 'Current and forecasted weather conditions affecting safety. E.g., high winds (crane operations), extreme heat (heat illness), rain/ice (slip hazards), lightning.'
  }, {
    id: 'siteCongestion',
    label: 'Site Congestion',
    weight: 3,
    dataAvailable: true,
    mapFromFactor: 'weather',
    tooltip: 'Density of workers and activities in the available space. E.g., multiple trades in same area, limited access routes, overlapping work zones.'
  }, {
    id: 'hazardousMaterials',
    label: 'Hazardous Materials',
    weight: 4,
    dataAvailable: true,
    mapFromFactor: 'weather',
    tooltip: 'Presence and handling of hazardous substances on site. E.g., lead paint, asbestos, silica dust, chemical solvents, flammable materials.'
  }, {
    id: 'heavyEquipmentDensity',
    label: 'Heavy Equipment Density',
    weight: 3,
    dataAvailable: true,
    mapFromFactor: 'weather',
    tooltip: 'Number of active cranes, hoists, and heavy machinery operating simultaneously. E.g., 4 tower cranes = higher struck-by and rigging risk than 1 crane.'
  }]
}, {
  id: 'safetyCultureEngagement',
  label: 'Safety Culture & Engagement',
  icon: '🤝',
  tooltip: 'Behavioral and cultural safety signals — overall safety culture indicators like voluntary reporting, stop-work authority usage, and peer coaching. Click to expand parameters.',
  params: [{
    id: 'safetyCultureIndex',
    label: 'Safety Culture Index',
    weight: 4,
    dataAvailable: true,
    mapFromFactor: 'workers',
    tooltip: 'Composite indicator of safety culture health. E.g., stop-work authority usage, voluntary hazard reporting, peer-to-peer safety coaching, safety suggestion rate.'
  }]
}];
// ─── DERIVED CATEGORY SPLITS ────────────────────────────────────────────────
interface CategorySplit {
  cat: CategoryDef;
  liveParams: ParamDef[];
  pendingParams: ParamDef[];
  pendingWeight: number;
  expandedColCount: number; // live columns + (1 if has pending)
}
function getCategorySplit(cat: CategoryDef): CategorySplit {
  const liveParams = cat.params.filter(p => p.dataAvailable);
  const pendingParams = cat.params.filter(p => !p.dataAvailable);
  const pendingWeight = pendingParams.reduce((s, p) => s + p.weight, 0);
  const expandedColCount = liveParams.length + (pendingParams.length > 0 ? 1 : 0);
  return {
    cat,
    liveParams,
    pendingParams,
    pendingWeight,
    expandedColCount
  };
}
// ─── CONSTANTS ──────────────────────────────────────────────────────────────
const SEVERITY_ORDER: Record<FactorSeverity, number> = {
  high: 3,
  medium: 2,
  low: 1,
  none: 0
};
const SEVERITY_NUMERIC: Record<FactorSeverity, number> = {
  high: 95,
  medium: 65,
  low: 35,
  none: 10
};
const SEVERITY_LABELS: Record<FactorSeverity, string> = {
  high: 'High',
  medium: 'Med',
  low: 'Low',
  none: '—'
};
const SEVERITY_CELL_STYLES: Record<FactorSeverity | 'no_data', {
  bg: string;
  text: string;
  border: string;
}> = {
  high: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    border: 'border-red-200'
  },
  medium: {
    bg: 'bg-orange-50',
    text: 'text-orange-800',
    border: 'border-orange-200'
  },
  low: {
    bg: 'bg-yellow-50',
    text: 'text-yellow-800',
    border: 'border-yellow-200'
  },
  none: {
    bg: 'bg-gray-50',
    text: 'text-gray-400',
    border: 'border-gray-100'
  },
  no_data: {
    bg: 'bg-gray-100',
    text: 'text-gray-400',
    border: 'border-dashed border-gray-300'
  }
};
// Category header tint colors for expanded state
const CAT_HEADER_TINTS: Record<string, string> = {
  planningControls: 'bg-blue-50 border-blue-200',
  observationsInspections: 'bg-teal-50 border-teal-200',
  workforceContractors: 'bg-orange-50 border-orange-200',
  incidentsLoopClosure: 'bg-red-50 border-red-200',
  inherentRiskEnvironment: 'bg-cyan-50 border-cyan-200',
  safetyCultureEngagement: 'bg-slate-50 border-slate-200'
};
const CAT_LEFT_BORDER: Record<string, string> = {
  planningControls: 'border-l-[3px] border-l-blue-400',
  observationsInspections: 'border-l-[3px] border-l-teal-400',
  workforceContractors: 'border-l-[3px] border-l-orange-400',
  incidentsLoopClosure: 'border-l-[3px] border-l-red-400',
  inherentRiskEnvironment: 'border-l-[3px] border-l-cyan-400',
  safetyCultureEngagement: 'border-l-[3px] border-l-slate-400'
};
const CAT_LEFT_BORDER_THIN: Record<string, string> = {
  planningControls: 'border-l border-l-blue-200',
  observationsInspections: 'border-l border-l-teal-200',
  workforceContractors: 'border-l border-l-orange-200',
  incidentsLoopClosure: 'border-l border-l-red-200',
  inherentRiskEnvironment: 'border-l border-l-cyan-200',
  safetyCultureEngagement: 'border-l border-l-slate-200'
};
const CAT_PARAM_TINTS: Record<string, string> = {
  planningControls: 'bg-blue-50/25',
  observationsInspections: 'bg-teal-50/25',
  workforceContractors: 'bg-orange-50/25',
  incidentsLoopClosure: 'bg-red-50/25',
  inherentRiskEnvironment: 'bg-cyan-50/25',
  safetyCultureEngagement: 'bg-slate-50/25'
};
// ─── HELPERS ────────────────────────────────────────────────────────────────
function getCategoryWeight(cat: CategoryDef): number {
  return cat.params.reduce((sum, p) => sum + p.weight, 0);
}
function getParamSeverity(area: EnrichedRiskArea, param: ParamDef): {
  severity: FactorSeverity;
  isNoData: boolean;
} {
  if (!param.dataAvailable || !param.mapFromFactor) {
    return {
      severity: 'none',
      isNoData: true
    };
  }
  const sev = area.riskFactorScores[param.mapFromFactor];
  const isNoData = area.dataConfidence === 'low' && sev === 'none';
  return {
    severity: sev,
    isNoData
  };
}
function getWorstSeverity(area: EnrichedRiskArea, params: ParamDef[]): {
  worst: FactorSeverity;
  allNoData: boolean;
} {
  let worst: FactorSeverity = 'none';
  let hasAnyData = false;
  for (const p of params) {
    const {
      severity,
      isNoData
    } = getParamSeverity(area, p);
    if (!isNoData) {
      hasAnyData = true;
      if (SEVERITY_ORDER[severity] > SEVERITY_ORDER[worst]) worst = severity;
    }
  }
  return {
    worst,
    allNoData: !hasAnyData
  };
}
function getSeverityFraction(area: EnrichedRiskArea, params: ParamDef[]): {
  count: number;
  total: number;
  dataCount: number;
} {
  let count = 0;
  let dataCount = 0;
  for (const p of params) {
    const {
      severity,
      isNoData
    } = getParamSeverity(area, p);
    if (!isNoData) {
      dataCount++;
      if (severity !== 'none') count++;
    }
  }
  return {
    count,
    total: params.length,
    dataCount
  };
}
function getAreaTrend(area: EnrichedRiskArea): 'up' | 'down' | 'flat' {
  const history = area.scoreHistory;
  if (history.length < 7) return 'flat';
  const recent = history[history.length - 1].score;
  const weekAgo = history[Math.max(0, history.length - 7)].score;
  const diff = recent - weekAgo;
  if (diff > 3) return 'up';
  if (diff < -3) return 'down';
  return 'flat';
}
function computeWeightedContribution(severity: FactorSeverity, weight: number): number {
  return Math.round(SEVERITY_NUMERIC[severity] * weight / 100);
}
// Contextual reasons for parameter scores
function getParamScoreReason(paramId: string, severity: FactorSeverity, isNoData: boolean): string {
  if (isNoData) return 'Data integration pending — score unavailable.';
  if (severity === 'none') return 'No significant risk signal detected for this parameter.';
  const reasons: Record<string, Record<string, string>> = {
    observationFrequency: {
      high: 'Very few safety observations recorded recently — low observation frequency may indicate gaps in safety monitoring.',
      medium: 'Observation frequency is below target — some monitoring gaps may exist.',
      low: 'Observation frequency is within acceptable range.'
    },
    observationSeverity: {
      high: 'Recent observations flagged serious hazards — multiple high-severity findings reported.',
      medium: 'Some moderate-severity observations noted — requires follow-up.',
      low: 'Observation severity is low — minor findings only.'
    },
    openObservationCount: {
      high: 'Large backlog of unresolved observations — corrective actions are overdue.',
      medium: 'Several open observations awaiting resolution.',
      low: 'Few open observations — most findings have been addressed.'
    },
    positiveObservationRatio: {
      high: 'Very low ratio of positive observations — safe behaviors are not being recognized.',
      medium: 'Positive observation ratio is below target.',
      low: 'Healthy mix of positive and corrective observations.'
    },
    ptpQuality: {
      high: 'Pre-task plans lack specificity — generic hazard identification and missing controls.',
      medium: 'PTP quality is inconsistent — some plans lack site-specific detail.',
      low: 'PTPs are well-written with specific hazards and controls identified.'
    },
    ptpEngagement: {
      high: 'Crew participation in pre-task planning is critically low — many workers not engaged.',
      medium: 'PTP engagement is below target — not all crew members are participating.',
      low: 'Strong crew engagement in pre-task planning activities.'
    },
    workerCompetency: {
      high: 'Significant training gaps identified — certifications expired or missing for key tasks.',
      medium: 'Some workers have training gaps or expiring certifications.',
      low: 'Workforce is well-trained with current certifications.'
    },
    fatigueRisk: {
      high: 'Extended shifts and insufficient rest periods detected — high fatigue exposure.',
      medium: 'Some fatigue risk indicators present — monitor shift patterns.',
      low: 'Work schedules are within safe fatigue limits.'
    },
    newWorkerRatio: {
      high: 'High proportion of new workers on site — increased risk from inexperience.',
      medium: 'Moderate number of new workers — additional supervision recommended.',
      low: 'Experienced crew with few new workers.'
    },
    crewStability: {
      high: 'High crew turnover disrupting safety continuity and team coordination.',
      medium: 'Some crew changes affecting team stability.',
      low: 'Stable crew composition supporting consistent safety practices.'
    },
    weatherSeverity: {
      high: 'Severe weather conditions active — high wind, extreme heat, or storms impacting safety.',
      medium: 'Moderate weather conditions — some activities may need adjustment.',
      low: 'Weather conditions are favorable for planned activities.'
    },
    noiseDust: {
      high: 'Excessive noise/dust levels — PPE compliance and exposure limits at risk.',
      medium: 'Elevated noise or dust — monitoring recommended.',
      low: 'Noise and dust levels within acceptable limits.'
    },
    siteCongestion: {
      high: 'Site is severely congested — overlapping trades creating hazardous conditions.',
      medium: 'Moderate site congestion — coordination needed between trades.',
      low: 'Site layout allows safe movement and work separation.'
    },
    openIncidents: {
      high: 'Multiple open incidents unresolved — investigation and corrective actions overdue.',
      medium: 'Some open incidents requiring attention.',
      low: 'Few or no open incidents — good incident closure rate.'
    },
    incidentRecurrence: {
      high: 'Repeated incidents of the same type — root causes not being addressed.',
      medium: 'Some recurring incident patterns detected.',
      low: 'No significant incident recurrence patterns.'
    },
    nearMissRatio: {
      high: 'High near-miss frequency signals elevated risk exposure before actual incidents.',
      medium: 'Moderate near-miss activity — investigate contributing factors.',
      low: 'Near-miss ratio is within normal range.'
    },
    correctiveActionClosure: {
      high: 'Corrective actions are significantly overdue — poor follow-through on fixes.',
      medium: 'Some corrective actions behind schedule.',
      low: 'Corrective actions are being closed on time.'
    },
    inspectionCompliance: {
      high: 'Inspection schedule significantly behind — required inspections missed or overdue.',
      medium: 'Some inspections behind schedule.',
      low: 'Inspections are on schedule and compliant.'
    },
    findingsSeverity: {
      high: 'Recent inspections revealed critical findings requiring immediate action.',
      medium: 'Moderate inspection findings — corrective actions needed.',
      low: 'Inspection findings are minor.'
    },
    reinspectionRate: {
      high: 'High reinspection rate — previous findings not adequately resolved.',
      medium: 'Some items requiring reinspection.',
      low: 'Low reinspection rate — findings resolved effectively.'
    },
    permitCurrency: {
      high: 'Multiple permits expired or missing — work may be non-compliant.',
      medium: 'Some permits approaching expiry — renewal needed.',
      low: 'All permits are current and valid.'
    },
    jhaCompleteness: {
      high: 'JHAs are incomplete or missing for active tasks — critical safety gaps.',
      medium: 'Some JHAs lack completeness — review and update needed.',
      low: 'JHAs are thorough and up to date.'
    },
    toolboxTalkAttendance: {
      high: 'Very low toolbox talk attendance — safety messaging not reaching crews.',
      medium: 'Toolbox talk attendance is below target.',
      low: 'Good toolbox talk attendance and engagement.'
    },
    documentExpiry: {
      high: 'High rate of expired safety documents — compliance risk.',
      medium: 'Some documents approaching or past expiry.',
      low: 'Document management is current.'
    },
    subSafetyScore: {
      high: 'Subcontractor safety performance is poor — history of violations or incidents.',
      medium: 'Subcontractor safety score is below average.',
      low: 'Subcontractor has a strong safety track record.'
    },
    subComplianceRate: {
      high: 'Subcontractor compliance rate is critically low — repeated non-conformances.',
      medium: 'Some subcontractor compliance gaps identified.',
      low: 'Subcontractor compliance is satisfactory.'
    },
    subIncidentRate: {
      high: 'Subcontractor incident rate is significantly above average.',
      medium: 'Subcontractor incident rate is slightly elevated.',
      low: 'Subcontractor incident rate is within acceptable range.'
    },
    equipmentCert: {
      high: 'Equipment certifications expired — unsafe equipment may be in use.',
      medium: 'Some equipment certifications nearing expiry.',
      low: 'All equipment certifications are current.'
    },
    materialStorage: {
      high: 'Material storage violations — hazardous materials improperly stored.',
      medium: 'Minor material storage issues identified.',
      low: 'Material storage meets compliance standards.'
    },
    craneHoistStatus: {
      high: 'Crane/hoist inspection overdue or deficiencies found.',
      medium: 'Crane/hoist status requires monitoring.',
      low: 'Crane/hoist equipment is in good condition.'
    },
    oshaCitationRisk: {
      high: 'Conditions likely to trigger OSHA citation — immediate correction needed.',
      medium: 'Some conditions may attract regulatory attention.',
      low: 'Low regulatory citation risk.'
    },
    communityComplaints: {
      high: 'Multiple community complaints received — noise, dust, or traffic issues.',
      medium: 'Some community complaints — mitigation may be needed.',
      low: 'Few or no community complaints.'
    },
    insuranceAudit: {
      high: 'Insurance audit readiness is poor — documentation gaps and compliance issues.',
      medium: 'Some areas need attention before next audit.',
      low: 'Well-prepared for insurance audit.'
    }
  };
  return reasons[paramId]?.[severity] || `Parameter is rated ${severity} — review contributing factors.`;
}
// Project-level aggregation
interface ProjectGroup {
  projectName: string;
  regionId: string;
  regionName: string;
  activities: EnrichedRiskArea[];
  projectScore: number;
  sifPotential: boolean;
}
function groupAreasByProject(areas: EnrichedRiskArea[]): ProjectGroup[] {
  const groups = new Map<string, EnrichedRiskArea[]>();
  areas.forEach(area => {
    if (!groups.has(area.project)) {
      groups.set(area.project, []);
    }
    groups.get(area.project)!.push(area);
  });
  return Array.from(groups.entries()).map(([projectName, activities]) => {
    const region = getRegionForProject(projectName);
    // Calculate weighted average score
    let totalWeightedScore = 0;
    let totalWeight = 0;
    let sifPotential = false;
    activities.forEach(a => {
      // Use the score itself as the weight (higher risk activities pull the average up more)
      const weight = Math.max(10, a.riskScore);
      totalWeightedScore += a.riskScore * weight;
      totalWeight += weight;
      if (a.sifPotential) sifPotential = true;
    });
    const projectScore = totalWeight > 0 ? Math.round(totalWeightedScore / totalWeight) : 0;
    return {
      projectName,
      regionId: region?.id || 'unknown',
      regionName: region?.name || 'Unknown Business Unit',
      activities: activities.sort((a, b) => b.riskScore - a.riskScore),
      projectScore,
      sifPotential
    };
  });
}
function getProjectParamSeverity(project: ProjectGroup, param: ParamDef): {
  severity: FactorSeverity;
  isNoData: boolean;
  hasWorstCase: boolean;
} {
  if (!param.dataAvailable || !param.mapFromFactor) {
    return {
      severity: 'none',
      isNoData: true,
      hasWorstCase: false
    };
  }
  const factorKey = param.mapFromFactor;
  let highCount = 0;
  let totalWeight = 0;
  let weightedSeveritySum = 0;
  let dataCount = 0;
  project.activities.forEach(a => {
    const sev = a.riskFactorScores[factorKey];
    const isNoData = a.dataConfidence === 'low' && sev === 'none';
    if (!isNoData) {
      dataCount++;
      if (sev === 'high') highCount++;
      const weight = Math.max(10, a.riskScore);
      weightedSeveritySum += SEVERITY_ORDER[sev] * weight;
      totalWeight += weight;
    }
  });
  if (dataCount === 0) {
    return {
      severity: 'none',
      isNoData: true,
      hasWorstCase: false
    };
  }
  const avgVal = weightedSeveritySum / totalWeight;
  let result: FactorSeverity = 'none';
  if (avgVal >= 2.5) result = 'high';else if (avgVal >= 1.5) result = 'medium';else if (avgVal >= 0.5) result = 'low';
  // Only flag worst-case when averaged severity is 'low' and 3+ activities are high
  const hasWorstCase = result === 'low' && highCount >= 3;
  return {
    severity: result,
    isNoData: false,
    hasWorstCase
  };
}
function getProjectWorstSeverity(project: ProjectGroup, params: ParamDef[]): {
  worst: FactorSeverity;
  allNoData: boolean;
  hasWorstCase: boolean;
} {
  let worst: FactorSeverity = 'none';
  let hasAnyData = false;
  let hasWorstCase = false;
  for (const p of params) {
    const {
      severity,
      isNoData,
      hasWorstCase: paramHasWorstCase
    } = getProjectParamSeverity(project, p);
    if (!isNoData) {
      hasAnyData = true;
      if (paramHasWorstCase) hasWorstCase = true;
      if (SEVERITY_ORDER[severity] > SEVERITY_ORDER[worst]) worst = severity;
    }
  }
  return {
    worst,
    allNoData: !hasAnyData,
    hasWorstCase
  };
}
function getActivityBreakdownForParam(project: ProjectGroup, param: ParamDef): {
  high: number;
  medium: number;
  low: number;
  none: number;
} {
  const counts = {
    high: 0,
    medium: 0,
    low: 0,
    none: 0
  };
  if (!param.dataAvailable || !param.mapFromFactor) return counts;
  const factorKey = param.mapFromFactor;
  project.activities.forEach(a => {
    const sev = a.riskFactorScores[factorKey];
    if (sev === 'high' || sev === 'critical') counts.high++;else if (sev === 'medium') counts.medium++;else if (sev === 'low') counts.low++;else counts.none++;
  });
  return counts;
}
function getProjectSeverityFraction(project: ProjectGroup, params: ParamDef[]): {
  count: number;
  total: number;
  dataCount: number;
} {
  let count = 0;
  let dataCount = 0;
  for (const p of params) {
    const {
      severity,
      isNoData
    } = getProjectParamSeverity(project, p);
    if (!isNoData) {
      dataCount++;
      if (severity !== 'none') count++;
    }
  }
  return {
    count,
    total: params.length,
    dataCount
  };
}
function generateCSV(projects: ProjectGroup[], categories: CategoryDef[]): string {
  const allParams = categories.flatMap(c => c.params);
  const headers = ['Project', 'Business Unit', 'Activity Count', ...allParams.map(p => p.label), 'Project Score'];
  const rows = projects.map(p => [p.projectName, p.regionName, p.activities.length.toString(), ...allParams.map(param => {
    const {
      severity,
      isNoData
    } = getProjectParamSeverity(p, param);
    return isNoData ? 'no_data' : severity;
  }), p.projectScore.toString()]);
  return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
}
// ─── CELL COMPONENTS ────────────────────────────────────────────────────────
function ContractorCell({
  area,
  rowIdx,
  isNested = false,
  colWidth
}: {
  area: EnrichedRiskArea;
  rowIdx: number;
  isNested?: boolean;
  colWidth?: number;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  return <td className={`sticky left-0 p-0 border-r border-gray-200 overflow-visible ${showTooltip ? 'z-[50]' : 'z-10'} ${rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${isNested ? 'border-l-2 border-l-gray-300' : ''}`} onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} data-id="element-2392">
      <div className={`overflow-hidden ${isNested ? 'pl-8 pr-3 py-2.5' : 'px-3 py-2.5'}`} style={colWidth ? {
      width: colWidth
    } : undefined} data-id="element-2393">
        <div className="flex items-center gap-1.5 min-w-0" data-id="element-2394">
          <span className="flex-shrink-0 text-sm leading-none" role="img" aria-label="contractor" data-id="element-2395">
            👷
          </span>
          <div className="min-w-0" data-id="element-2396">
            <span className="text-xs font-medium text-gray-700 truncate block" data-id="element-2397">
              {area.contractors[0] || 'Unknown'}
            </span>
            <span className="text-[10px] text-gray-400 truncate block" data-id="element-2398">
              {area.floor} · {area.location}
            </span>
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-2399">
        {showTooltip && <motion.div initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} className="absolute z-[100] bottom-full left-2 mb-1 w-56 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl pointer-events-none" data-id="element-2400">
            <div className="absolute -bottom-1 left-4 w-2 h-2 bg-gray-900 rotate-45" data-id="element-2401" />
            <p className="font-bold mb-1" data-id="element-2402">{area.contractors[0] || 'Unknown'}</p>
            <p className="text-gray-300" data-id="element-2403">
              {area.floor} · {area.location}
            </p>
          </motion.div>}
      </AnimatePresence>
    </td>;
}
function SeverityCell({
  severity,
  isNoData,
  weight,
  score,
  paramLabel,
  paramId,
  className = '',
  hasWorstCase = false,
  activityBreakdown,
  isActivity = false,
  onClick
}: {
  severity: FactorSeverity;
  isNoData: boolean;
  weight: number;
  score: number;
  paramLabel: string;
  paramId: string;
  className?: string;
  hasWorstCase?: boolean;
  activityBreakdown?: {
    high: number;
    medium: number;
    low: number;
    none: number;
  };
  isActivity?: boolean;
  onClick?: () => void;
}) {
  const style = isNoData ? SEVERITY_CELL_STYLES.no_data : SEVERITY_CELL_STYLES[severity];
  return <td className={`px-2 py-2.5 text-center cursor-pointer hover:bg-gray-50 transition-colors ${className}`} onClick={onClick} data-id="element-2404">
      <div className="relative inline-block" data-id="element-2405">
        <div className={`inline-flex items-center justify-center ${isActivity ? 'min-w-[36px] h-5 px-1.5 rounded text-[10px] font-semibold border-0' : 'min-w-[44px] h-7 px-2 rounded-md text-xs font-bold border'} ${style.bg} ${style.text} ${isActivity ? '' : style.border}`} data-id="element-2406">
          {isNoData ? '▧' : SEVERITY_LABELS[severity]}
        </div>
      </div>
    </td>;
}
function PendingHeaderCell({
  pendingParams,
  className = ''
}: {
  pendingParams: ParamDef[];
  className?: string;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const totalWeight = pendingParams.reduce((s, p) => s + p.weight, 0);
  return <th className={`relative px-1.5 py-2 text-center min-w-[60px] ${className}`} onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} data-id="element-2407">
      <span className="text-[10px] font-semibold text-gray-400 cursor-default" data-id="element-2408">
        +{pendingParams.length}
        <span className="font-mono ml-0.5" data-id="element-2409">▧</span>
      </span>

      <AnimatePresence data-id="element-2410">
        {showTooltip && <motion.div initial={{
        opacity: 0,
        y: 4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 4
      }} className="absolute z-50 top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-gray-900 text-white text-xs rounded-lg px-3 py-2.5 shadow-xl pointer-events-none" data-id="element-2411">
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" data-id="element-2412" />
            <p className="font-bold mb-0.5" data-id="element-2413">
              {pendingParams.length} pending · {totalWeight}% weight
            </p>
            <div className="mt-1.5 space-y-1.5" data-id="element-2414">
              {pendingParams.map(p => <div key={p.id} data-id="element-2415">
                  <div className="flex items-center gap-1.5" data-id="element-2416">
                    <span className="text-gray-500 font-mono text-[10px]" data-id="element-2417">
                      ▧
                    </span>
                    <span className="font-semibold text-gray-200" data-id="element-2418">
                      {p.label}
                    </span>
                    <span className="text-gray-500 ml-auto" data-id="element-2419">{p.weight}%</span>
                  </div>
                  {p.pendingReason && <p className="text-[10px] text-gray-400 pl-4 mt-0.5" data-id="element-2420">
                      {p.pendingReason}
                    </p>}
                </div>)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </th>;
}
function PendingCell({
  pendingParams,
  className = ''
}: {
  pendingParams: ParamDef[];
  className?: string;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const totalWeight = pendingParams.reduce((s, p) => s + p.weight, 0);
  return <td className={`relative px-1.5 py-2.5 text-center ${className}`} onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} data-id="element-2421">
      <div className="inline-flex items-center justify-center min-w-[36px] h-7 px-1.5 rounded-md text-[10px] font-bold border border-dashed border-gray-300 bg-gray-100 text-gray-400" data-id="element-2422">
        ▧
      </div>

      <AnimatePresence data-id="element-2423">
        {showTooltip && <motion.div initial={{
        opacity: 0,
        y: 4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 4
      }} className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl pointer-events-none" data-id="element-2424">
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" data-id="element-2425" />
            <p className="font-bold mb-1" data-id="element-2426">
              {pendingParams.length} parameters pending
            </p>
            <p className="text-gray-400 text-[10px] mb-1.5" data-id="element-2427">
              Combined weight: {totalWeight}%
            </p>
            {pendingParams.map(p => <div key={p.id} className="flex items-center justify-between py-0.5" data-id="element-2428">
                <span className="text-gray-300" data-id="element-2429">{p.label}</span>
                <span className="text-gray-500" data-id="element-2430">{p.weight}%</span>
              </div>)}
          </motion.div>}
      </AnimatePresence>
    </td>;
}
function CollapsedCategoryCell({
  worst,
  allNoData,
  count,
  total,
  dataCount,
  categoryLabel,
  params,
  area,
  className = '',
  hasWorstCase = false,
  onClick
}: {
  worst: FactorSeverity;
  allNoData: boolean;
  count: number;
  total: number;
  dataCount: number;
  categoryLabel: string;
  params: ParamDef[];
  area?: EnrichedRiskArea;
  className?: string;
  hasWorstCase?: boolean;
  onClick?: () => void;
}) {
  const style = allNoData ? SEVERITY_CELL_STYLES.no_data : SEVERITY_CELL_STYLES[worst];
  return <td className={`relative px-2 py-2.5 text-center cursor-pointer hover:bg-gray-50 transition-colors ${className}`} onClick={onClick} data-id="element-2431">
      <div className="relative inline-block" data-id="element-2432">
        <div className={`inline-flex items-center gap-1.5 justify-center min-w-[56px] h-7 px-2 rounded-md text-xs font-bold border ${style.bg} ${style.text} ${style.border}`} data-id="element-2433">
          {allNoData ? <>▧</> : worst === 'none' ? '—' : <>{SEVERITY_LABELS[worst]}</>}
        </div>
      </div>
    </td>;
}
function TrendBadge({
  trend
}: {
  trend: 'up' | 'down' | 'flat';
}) {
  if (trend === 'up') return <TrendingUp size={13} className="text-red-500" data-id="element-2434" />;
  if (trend === 'down') return <TrendingDown size={13} className="text-emerald-500" data-id="element-2435" />;
  return <Minus size={13} className="text-gray-400" data-id="element-2436" />;
}
function InfluenceCell({
  label,
  weight,
  onClick,
  children,
  className = ''
}: {
  label: string;
  weight: number;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return <td className={`relative px-2 py-2 text-center cursor-pointer hover:bg-indigo-100/60 transition-colors ${className}`} onClick={onClick} data-id="element-2437">
      {children}
    </td>;
}
function getDefaultCategoryWeights(categories: CategoryDef[]): Record<string, number> {
  const rawWeights: Record<string, number> = {};
  let rawTotal = 0;
  categories.forEach(cat => {
    const sum = cat.params.reduce((s, p) => s + p.weight, 0);
    rawWeights[cat.id] = sum;
    rawTotal += sum;
  });
  const defaultWeights: Record<string, number> = {};
  if (rawTotal === 0) return defaultWeights;
  let allocated = 0;
  const keys = Object.keys(rawWeights);
  keys.forEach((key, idx) => {
    if (idx === keys.length - 1) {
      defaultWeights[key] = 100 - allocated;
    } else {
      const normalized = Math.floor(rawWeights[key] / rawTotal * 100);
      defaultWeights[key] = normalized;
      allocated += normalized;
    }
  });
  return defaultWeights;
}
// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────
interface RiskMatrixProps {
  onSelectArea?: (area: EnrichedRiskArea) => void;
}
export function RiskMatrix({
  onSelectArea
}: RiskMatrixProps) {
  const [categories, setCategories] = useState<CategoryDef[]>(DEFAULT_CATEGORIES);
  const [showCustomization, setShowCustomization] = useState(false);
  const defaultCategoryWeights = useMemo(() => getDefaultCategoryWeights(categories), [categories]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [modalExpandedCategories, setModalExpandedCategories] = useState<Set<string>>(new Set());
  const [modalState, setModalState] = useState<{
    projectName: string;
    tab: 'quick' | 'detailed';
    scrollToParamId?: string;
  } | null>(null);
  useEffect(() => {
    if (modalState?.tab === 'detailed' && modalState.scrollToParamId) {
      const cat = categories.find(c => c.params.some(p => p.id === modalState.scrollToParamId));
      if (cat) {
        setModalExpandedCategories(prev => new Set(prev).add(cat.id));
        // Scroll to the param element after a short delay to allow DOM to render
        const scrollTimer = setTimeout(() => {
          const el = document.getElementById(`param-${modalState.scrollToParamId}`);
          if (el) {
            el.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
            // Brief highlight flash
            el.classList.add('ring-2', 'ring-indigo-400', 'ring-offset-1');
            setTimeout(() => {
              el.classList.remove('ring-2', 'ring-indigo-400', 'ring-offset-1');
            }, 2000);
          } else {
            // If param element not found, scroll to the category section
            const catEl = document.getElementById(`modal-cat-${cat.id}`);
            if (catEl) {
              catEl.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }
          }
        }, 150);
        return () => clearTimeout(scrollTimer);
      }
    }
  }, [modalState, categories]);
  const [sortColumn, setSortColumn] = useState<string>('score');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [selectedModalRecommendation, setSelectedModalRecommendation] = useState<Recommendation | null>(null);
  const [showPendingPopover, setShowPendingPopover] = useState(false);
  const [showTuning, setShowTuning] = useState(false);
  const [categoryWeights, setCategoryWeights] = useState<Record<string, number>>({});
  // Initialize weights when categories change
  useEffect(() => {
    setCategoryWeights(getDefaultCategoryWeights(categories));
  }, [categories]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // First column resizable width
  const [firstColWidth, setFirstColWidth] = useState(300);
  const firstColWidthRef = useRef(300);
  const isResizing = useRef(false);
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isResizing.current = true;
    const startX = e.clientX;
    const startWidth = firstColWidthRef.current;
    const onMouseMove = (ev: MouseEvent) => {
      if (!isResizing.current) return;
      ev.preventDefault();
      const delta = ev.clientX - startX;
      const newWidth = Math.max(120, Math.min(400, startWidth + delta));
      firstColWidthRef.current = newWidth;
      setFirstColWidth(newWidth);
    };
    const onMouseUp = () => {
      isResizing.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);
  // Filters
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModalExport, setShowModalExport] = useState(false);
  const modalExportRef = useRef<HTMLDivElement>(null);
  // Close modal export menu on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (modalExportRef.current && !modalExportRef.current.contains(e.target as Node)) {
        setShowModalExport(false);
      }
    };
    if (showModalExport) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showModalExport]);
  // Escape key exits fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isFullscreen]);
  // Recompute categories with updated weights (proportional redistribution within each category)
  const tunedCategories = useMemo(() => {
    return categories.map(cat => {
      const defaultCatWeight = defaultCategoryWeights[cat.id] || 0;
      const newCatWeight = categoryWeights[cat.id] ?? defaultCatWeight;
      if (defaultCatWeight === 0 || newCatWeight === defaultCatWeight) return cat;
      const scale = newCatWeight / defaultCatWeight;
      const scaledParams = cat.params.map(p => ({
        ...p,
        weight: Math.round(p.weight * scale)
      }));
      // Fix rounding: adjust last param to make sum exact
      const scaledSum = scaledParams.reduce((s, p) => s + p.weight, 0);
      if (scaledSum !== newCatWeight && scaledParams.length > 0) {
        scaledParams[scaledParams.length - 1].weight += newCatWeight - scaledSum;
      }
      return {
        ...cat,
        params: scaledParams
      };
    });
  }, [categoryWeights, categories, defaultCategoryWeights]);
  const tunedCategorySplits = useMemo(() => tunedCategories.map(getCategorySplit), [tunedCategories]);
  // Category weights for the tuning panel
  const tuningPanelWeights = useMemo(() => categories.map(cat => ({
    id: cat.id,
    label: cat.label,
    icon: cat.icon,
    weight: categoryWeights[cat.id] ?? defaultCategoryWeights[cat.id],
    defaultWeight: defaultCategoryWeights[cat.id]
  })), [categories, categoryWeights, defaultCategoryWeights]);
  const handleWeightsChange = useCallback((newWeights: Record<string, number>) => {
    setCategoryWeights(newWeights);
  }, []);
  const toggleCategory = (catId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);else next.add(catId);
      return next;
    });
  };
  const expandAll = () => setExpandedCategories(new Set(categories.map(c => c.id)));
  const collapseAll = () => setExpandedCategories(new Set());
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };
  // Group and filter projects
  const filteredAndSortedProjects = useMemo(() => {
    let projects = groupAreasByProject(ENRICHED_MOCK_RISK_AREAS);
    // Apply Region Filter
    if (selectedRegion) {
      projects = projects.filter(p => p.regionId === selectedRegion);
    }
    // Apply Search Filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      projects = projects.filter(p => p.projectName.toLowerCase().includes(query) || p.activities.some(a => a.contractors.some(c => c.toLowerCase().includes(query))));
    }
    // Sort Projects
    projects.sort((a, b) => {
      let valA: number, valB: number;
      if (sortColumn === 'score') {
        valA = a.projectScore;
        valB = b.projectScore;
      } else if (sortColumn === 'name') {
        return sortDirection === 'asc' ? a.projectName.localeCompare(b.projectName) : b.projectName.localeCompare(a.projectName);
      } else {
        const param = categories.flatMap(c => c.params).find(p => p.id === sortColumn);
        if (param?.mapFromFactor) {
          const sevA = getProjectParamSeverity(a, param).severity;
          const sevB = getProjectParamSeverity(b, param).severity;
          valA = SEVERITY_ORDER[sevA];
          valB = SEVERITY_ORDER[sevB];
        } else {
          valA = 0;
          valB = 0;
        }
      }
      return sortDirection === 'asc' ? valA - valB : valB - valA;
    });
    return projects;
  }, [selectedRegion, searchQuery, sortColumn, sortDirection, categories]);
  // Compute per-region stats (project count + high risk flag)
  const regionStats = useMemo(() => {
    const allProjects = groupAreasByProject(ENRICHED_MOCK_RISK_AREAS);
    const stats: Record<string, {
      count: number;
      highRiskCount: number;
      hasHighRisk: boolean;
    }> = {};
    REGIONS.forEach(r => {
      const regionProjects = allProjects.filter(p => p.regionId === r.id);
      const highRiskProjects = regionProjects.filter(p => p.projectScore >= 70);
      stats[r.id] = {
        count: regionProjects.length,
        highRiskCount: highRiskProjects.length,
        hasHighRisk: highRiskProjects.length > 0
      };
    });
    return stats;
  }, []);
  const totalProjects = filteredAndSortedProjects.length;
  const totalParams = categories.reduce((sum, c) => sum + c.params.length, 0);
  const liveParamCount = categories.reduce((sum, c) => sum + c.params.filter(p => p.dataAvailable).length, 0);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
    };
    if (showExportMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);
  const triggerDownload = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], {
      type: mimeType
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const handleDownloadCSV = () => {
    const csv = generateCSV(filteredAndSortedProjects, categories);
    triggerDownload(csv, 'risk-matrix.csv', 'text/csv;charset=utf-8;');
    setToastMessage('Risk matrix downloaded as CSV');
    setShowToast(true);
    setShowExportMenu(false);
  };
  const handleDownloadExcel = () => {
    const allParams = categories.flatMap(c => c.params);
    const headers = ['Project', 'Business Unit', 'Activity Count', ...allParams.map(p => p.label), 'Project Score'];
    const rows = filteredAndSortedProjects.map(p => [p.projectName, p.regionName, p.activities.length.toString(), ...allParams.map(param => {
      const {
        severity,
        isNoData
      } = getProjectParamSeverity(p, param);
      return isNoData ? 'no_data' : severity;
    }), p.projectScore.toString()]);
    const tableRows = rows.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join('')}</tr>`).join('');
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="UTF-8"></head><body><table><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>${tableRows}</tbody></table></body></html>`;
    triggerDownload(html, 'risk-matrix.xls', 'application/vnd.ms-excel');
    setToastMessage('Risk matrix downloaded as Excel');
    setShowToast(true);
    setShowExportMenu(false);
  };
  const SortIcon = ({
    column
  }: {
    column: string;
  }) => {
    if (sortColumn !== column) return <ArrowUpDown size={12} className="text-gray-300" data-id="element-2438" />;
    return sortDirection === 'desc' ? <ArrowDown size={12} className="text-indigo-500" data-id="element-2439" /> : <ArrowUp size={12} className="text-indigo-500" data-id="element-2440" />;
  };
  // Check if any category is expanded (to show the two-row header)
  const anyExpanded = expandedCategories.size > 0;
  return <div className={`flex flex-col bg-white transition-all duration-300 ${isFullscreen ? 'fixed inset-0 z-[60]' : 'h-full'}`} data-id="element-2441">
      {/* Combined Toolbar: Region Filter + Search + Actions */}
      <div className="flex flex-col border-b border-gray-200 bg-gray-50 flex-shrink-0" data-id="element-2442">
        {/* Row 1: BU pills */}
        <div className="flex items-center gap-2 px-4 pt-1.5 pb-1 overflow-x-auto no-scrollbar" data-id="element-2443">
          <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider flex-shrink-0" data-id="element-2444">
            <MapPin size={12} className="inline -mt-0.5 mr-0.5" data-id="element-2445" />
            Business Unit
          </span>
          <div className="w-px h-4 bg-gray-300 flex-shrink-0" data-id="element-2446" />
          {/* All Projects pill */}
          {(() => {
          const allProjects = groupAreasByProject(ENRICHED_MOCK_RISK_AREAS);
          const totalCount = allProjects.length;
          const totalHighRisk = allProjects.filter(p => p.projectScore >= 70).length;
          const isAllSelected = selectedRegion === null;
          return <button onClick={() => setSelectedRegion(null)} title={`All Projects: ${totalCount} projects, ${totalHighRisk} high risk`} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${isAllSelected ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100 hover:border-gray-300'}`} data-id="element-2447">
                All Projects
                <span className={`inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded-md text-[10px] font-bold ${isAllSelected ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-2448">
                  {totalCount} proj
                </span>
                {totalHighRisk > 0 && <span className={`inline-flex items-center gap-0.5 justify-center min-w-[20px] h-[20px] px-1 rounded-md text-[10px] font-bold ${isAllSelected ? 'bg-red-400/80 text-white' : 'bg-red-50 text-red-600 border border-red-200'}`} data-id="element-2449">
                    <AlertTriangle size={9} data-id="element-2450" />
                    {totalHighRisk} at risk
                  </span>}
              </button>;
        })()}
          {REGIONS.map(region => {
          const stats = regionStats[region.id];
          const isSelected = selectedRegion === region.id;
          return <button key={region.id} onClick={() => setSelectedRegion(region.id)} title={`${region.name} Business Unit: ${stats?.count || 0} projects, ${stats?.highRiskCount || 0} high risk`} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${isSelected ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100 hover:border-gray-300'}`} data-id="element-2451">
                {region.name}
                <span className={`inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded-md text-[10px] font-bold ${isSelected ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`} title={`${stats?.count || 0} projects in this business unit`} data-id="element-2452">
                  {stats?.count || 0} proj
                </span>
                {stats?.highRiskCount > 0 && <span className={`inline-flex items-center gap-0.5 justify-center min-w-[20px] h-[20px] px-1 rounded-md text-[10px] font-bold ${isSelected ? 'bg-red-400/80 text-white' : 'bg-red-50 text-red-600 border border-red-200'}`} title={`${stats.highRiskCount} high-risk projects`} data-id="element-2453">
                    <AlertTriangle size={9} data-id="element-2454" />
                    {stats.highRiskCount} at risk
                  </span>}
              </button>;
        })}
        </div>

        {/* Row 2: Search + Actions */}
        <div className="flex items-center justify-end gap-1.5 px-4 pb-1.5 pt-0.5" data-id="element-2455">
          {/* Search */}
          <div className="relative flex-shrink-0 mr-auto" data-id="element-2456">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2457" />
            <input type="text" placeholder="Search project..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-7 pr-2 py-1 text-xs border border-gray-300 rounded-lg w-44 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white" data-id="element-2458" />
          </div>

          {/* Action buttons */}
          <button onClick={() => setShowTuning(true)} className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors" data-id="element-2459">
            <SlidersHorizontal size={11} data-id="element-2460" />
            Tune
          </button>
          <button onClick={collapseAll} className="flex items-center gap-1 px-1.5 py-1 text-[11px] font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" title="Collapse All" data-id="element-2461">
            <Minimize2 size={11} data-id="element-2462" />
          </button>
          <button onClick={expandAll} className="flex items-center gap-1 px-1.5 py-1 text-[11px] font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" title="Expand All" data-id="element-2463">
            <Maximize2 size={11} data-id="element-2464" />
          </button>
          <div className="relative" ref={exportMenuRef} data-id="element-2465">
            <button onClick={() => setShowExportMenu(v => !v)} className="flex items-center gap-1 px-1.5 py-1 text-[11px] font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" title="Export" data-id="element-2466">
              <Download size={11} data-id="element-2467" />
              <ChevronDown size={9} data-id="element-2468" />
            </button>
            <AnimatePresence data-id="element-2469">
              {showExportMenu && <motion.div initial={{
              opacity: 0,
              y: -4
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} transition={{
              duration: 0.12
            }} className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1" data-id="element-2470">
                  <button onClick={handleDownloadCSV} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-2471">
                    <Download size={12} className="text-gray-400" data-id="element-2472" />
                    Download CSV
                  </button>
                  <button onClick={handleDownloadExcel} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-2473">
                    <Download size={12} className="text-gray-400" data-id="element-2474" />
                    Download Excel
                  </button>
                </motion.div>}
            </AnimatePresence>
          </div>
          <button onClick={() => setIsFullscreen(v => !v)} className={`flex items-center gap-1 px-1.5 py-1 text-[11px] font-medium rounded-md transition-colors ${isFullscreen ? 'text-indigo-700 bg-indigo-100 hover:bg-indigo-200' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`} title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'} data-id="element-2475">
            {isFullscreen ? <Shrink size={11} data-id="element-2476" /> : <Fullscreen size={11} data-id="element-2477" />}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto" data-id="element-2478">
        <table className="w-full text-sm border-collapse min-w-[800px]" data-id="element-2479">
          <thead className="sticky top-0 z-20 bg-white shadow-sm" data-id="element-2480">
            {/* ── ROW 1: Category group headers ── */}
            <tr className="border-b border-gray-100" data-id="element-2481">
              <th className="sticky left-0 z-30 bg-white p-0 text-left border-r border-gray-200" rowSpan={anyExpanded ? 2 : 1} data-id="element-2482">
                <div className="relative px-3 py-2.5 overflow-hidden" style={{
                width: firstColWidth
              }} data-id="element-2483">
                  <div className="flex items-center justify-between gap-1" data-id="element-2484">
                    <button onClick={() => handleSort('name')} className="flex items-center gap-1.5 text-xs font-bold text-gray-500 uppercase tracking-wider hover:text-gray-700 transition-colors" data-id="element-2485">
                      Project
                      <SortIcon column="name" data-id="element-2486" />
                    </button>
                  </div>
                  {/* Resize handle */}
                  <div onMouseDown={handleResizeStart} className="absolute right-0 top-0 bottom-0 w-[10px] cursor-col-resize z-40 group/handle" title="Drag to resize column" data-id="element-2487">
                    <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[3px] rounded-full opacity-0 group-hover/handle:opacity-100 bg-indigo-400 transition-opacity" data-id="element-2488" />
                  </div>
                </div>
              </th>

              {/* Score — moved early */}
              <th className="px-3 py-2.5 text-center min-w-[70px] border-r border-gray-200" rowSpan={anyExpanded ? 2 : 1} data-id="element-2489">
                <button onClick={() => handleSort('score')} className="flex items-center gap-1 justify-center text-xs font-bold text-gray-700 uppercase tracking-wider hover:text-gray-900 transition-colors" data-id="element-2490">
                  Risk Score
                  <SortIcon column="score" data-id="element-2491" />
                </button>
              </th>

              {tunedCategorySplits.map(({
              cat,
              liveParams,
              pendingParams,
              expandedColCount
            }) => {
              const isExpanded = expandedCategories.has(cat.id);
              const hasData = liveParams.length > 0;
              const catWeight = getCategoryWeight(cat);
              const tint = CAT_HEADER_TINTS[cat.id] || 'bg-gray-50 border-gray-200';
              const leftBorder = CAT_LEFT_BORDER[cat.id] || 'border-l border-gray-200';
              if (isExpanded) {
                return <th key={cat.id} colSpan={expandedColCount} className={`px-3 py-2 text-center border-b ${tint} ${leftBorder}`} data-id="element-2492">
                        <button onClick={() => toggleCategory(cat.id)} className="flex items-center gap-2 justify-center w-full" data-id="element-2493">
                          <ChevronDown size={16} className="text-gray-700 transition-transform" data-id="element-2494" />
                          <span className="text-[13px]" data-id="element-2495">{cat.icon}</span>
                          <span className="text-sm font-bold text-gray-800" data-id="element-2496">
                            {cat.label}
                          </span>
                          <span className="text-[10px] font-semibold text-gray-400 bg-white/60 px-1.5 py-0.5 rounded-full" data-id="element-2497">
                            {liveParams.length}
                          </span>
                          <span className="text-[10px] font-semibold text-gray-500 bg-white/60 px-1.5 py-0.5 rounded" data-id="element-2498">
                            {catWeight}%
                          </span>
                          {pendingParams.length > 0 && <span className="text-[10px] font-medium text-gray-400 ml-1" data-id="element-2499">
                              +{pendingParams.length} pending
                            </span>}
                        </button>
                      </th>;
              } else {
                return <th key={cat.id} className={`px-2 py-2.5 text-center min-w-[100px] ${leftBorder}`} rowSpan={anyExpanded ? 2 : 1} data-id="element-2500">
                        <div className="relative group/cat" data-id="element-2501">
                          <button onClick={() => toggleCategory(cat.id)} className="flex items-center gap-1 w-full justify-center hover:text-indigo-600 transition-colors" data-id="element-2502">
                            <span data-id="element-2503">{cat.icon}</span>
                            <span className={`text-xs font-bold ${hasData ? 'text-gray-700' : 'text-gray-400'}`} data-id="element-2504">
                              {cat.label}
                            </span>
                            <span className="text-[10px] font-semibold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full" data-id="element-2505">
                              {liveParams.length}
                            </span>
                            {!hasData && <span className="text-[9px] text-gray-400 font-mono" data-id="element-2506">
                                ▧
                              </span>}
                            <ChevronRight size={12} className="text-gray-400" data-id="element-2507" />
                          </button>
                          {cat.tooltip && <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 w-72 p-2.5 bg-gray-900 text-white text-[11px] leading-relaxed rounded-lg shadow-xl opacity-0 invisible group-hover/cat:opacity-100 group-hover/cat:visible transition-all duration-150 z-50 pointer-events-none" data-id="element-2508">
                              {cat.tooltip}
                              <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" data-id="element-2509" />
                            </div>}
                        </div>
                      </th>;
              }
            })}
            </tr>

            {/* ── ROW 2: Individual param headers (only when any category is expanded) ── */}
            {anyExpanded && <tr className="border-b border-gray-200" data-id="element-2510">
                {tunedCategorySplits.map(({
              cat,
              liveParams,
              pendingParams
            }) => {
              const isExpanded = expandedCategories.has(cat.id);
              if (!isExpanded) return null;
              const leftBorder = CAT_LEFT_BORDER[cat.id] || '';
              const tint = CAT_PARAM_TINTS[cat.id] || '';
              return <Fragment key={cat.id} data-id="element-2511">
                        {liveParams.map((param, idx) => <th key={param.id} className={`px-2 py-2 text-center min-w-[90px] ${idx === 0 ? leftBorder : ''} ${tint}`} data-id="element-2512">
                            <div className="relative group/param" data-id="element-2513">
                              <button onClick={() => handleSort(param.id)} className="flex items-center gap-1 justify-center text-xs font-semibold text-gray-600 hover:text-gray-800 transition-colors w-full" data-id="element-2514">
                                {param.aiEvaluated && <span className="text-[10px]" data-id="element-2515">🤖</span>}
                                {param.label}
                                <SortIcon column={param.id} data-id="element-2516" />
                              </button>
                              {param.tooltip && <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 w-64 p-2.5 bg-gray-900 text-white text-[11px] leading-relaxed rounded-lg shadow-xl opacity-0 invisible group-hover/param:opacity-100 group-hover/param:visible transition-all duration-150 z-50 pointer-events-none" data-id="element-2517">
                                  {param.tooltip}
                                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" data-id="element-2518" />
                                </div>}
                            </div>
                          </th>)}
                        {pendingParams.length > 0 && <PendingHeaderCell pendingParams={pendingParams} className={`${liveParams.length === 0 ? leftBorder : ''} ${tint}`} data-id="element-2519" />}
                      </Fragment>;
            })}
              </tr>}

            {/* Influence row removed — weights accessible via Tune button */}
          </thead>

          {/* ── BODY: Project Rows ── */}
          <tbody data-id="element-2520">
            {filteredAndSortedProjects.map((project, rowIdx) => {
            const band = getRiskBand(project.projectScore);
            const riskConfig = RISK_LEVEL_CONFIG[scoreToRiskLevel(project.projectScore)];
            // Project Trend (simplified for mock)
            const trend = 'flat';
            return <Fragment key={project.projectName} data-id="element-2521">
                  {/* PROJECT ROW */}
                  <tr className="border-b border-gray-200 transition-colors bg-gray-50 hover:bg-gray-100" data-id="element-2522">
                    {/* Project Name Cell */}
                    <td className="sticky left-0 p-0 border-r border-gray-300 z-10 bg-gray-50 cursor-pointer group/project" onClick={() => setModalState({
                  projectName: project.projectName,
                  tab: 'quick'
                })} data-id="element-2523">
                      <div className="px-3 py-3 overflow-hidden" style={{
                    width: firstColWidth
                  }} data-id="element-2524">
                        <div className="flex items-center gap-2" data-id="element-2525">
                          <div className="min-w-0" data-id="element-2526">
                            <div className="flex items-center gap-1.5" data-id="element-2527">
                              <Building2 size={14} className="text-indigo-400 flex-shrink-0 group-hover/project:text-indigo-600 transition-colors" data-id="element-2528" />
                              <span className="text-sm font-bold text-gray-900 truncate group-hover/project:text-indigo-600 group-hover/project:underline transition-colors" data-id="element-2529">
                                {project.projectName}
                              </span>
                              <ExternalLink size={14} className="text-indigo-600 opacity-0 group-hover/project:opacity-100 transition-opacity flex-shrink-0" data-id="element-2530" />
                            </div>
                            <div className="flex items-center gap-2 mt-0.5" data-id="element-2531">
                              <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200" data-id="element-2532">
                                {project.regionName}
                              </span>
                              <span className="text-[10px] text-gray-500" data-id="element-2533">
                                {project.activities.length} active
                                subcontractors
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Score Cell (Project Level) */}
                    <td className="px-3 py-3 text-center border-r border-gray-300 cursor-pointer group/score" onClick={() => setModalState({
                  projectName: project.projectName,
                  tab: 'quick'
                })} data-id="element-2534">
                      <div className="flex items-center justify-center" data-id="element-2535">
                        <span className={`inline-flex items-center justify-center min-w-[52px] h-9 px-3 rounded-lg text-base font-black tabular-nums shadow-sm ${riskConfig.bgColor} ${riskConfig.color} border border-black/5 group-hover/score:ring-2 group-hover/score:ring-indigo-500/50 group-hover/score:ring-offset-1 transition-all`} data-id="element-2536">
                          {project.projectScore}%
                        </span>
                      </div>
                    </td>

                    {/* Parameter Cells (Project Level) */}
                    {tunedCategorySplits.map(({
                  cat,
                  liveParams,
                  pendingParams
                }) => {
                  const isExpanded = expandedCategories.has(cat.id);
                  if (isExpanded) {
                    return <Fragment key={cat.id} data-id="element-2537">
                              {liveParams.map((param, idx) => {
                        const {
                          severity,
                          isNoData,
                          hasWorstCase
                        } = getProjectParamSeverity(project, param);
                        const activityBreakdown = getActivityBreakdownForParam(project, param);
                        return <SeverityCell key={param.id} severity={severity} isNoData={isNoData} weight={param.weight} score={project.projectScore} paramLabel={param.label} paramId={param.id} hasWorstCase={hasWorstCase} activityBreakdown={activityBreakdown} className={idx === 0 ? CAT_LEFT_BORDER_THIN[cat.id] || 'border-l border-gray-200' : ''} onClick={() => setModalState({
                          projectName: project.projectName,
                          tab: 'detailed',
                          scrollToParamId: param.id
                        })} data-id="element-2538" />;
                      })}
                              {pendingParams.length > 0 && <PendingCell pendingParams={pendingParams} data-id="element-2539" />}
                            </Fragment>;
                  } else {
                    const {
                      worst,
                      allNoData,
                      hasWorstCase
                    } = getProjectWorstSeverity(project, cat.params);
                    const {
                      count,
                      total,
                      dataCount
                    } = getProjectSeverityFraction(project, cat.params);
                    return <CollapsedCategoryCell key={cat.id} worst={worst} allNoData={allNoData} count={count} total={total} dataCount={dataCount} categoryLabel={cat.label} params={cat.params} hasWorstCase={hasWorstCase} className={CAT_LEFT_BORDER_THIN[cat.id] || 'border-l border-gray-200'} onClick={() => toggleCategory(cat.id)} data-id="element-2540" />;
                  }
                })}
                  </tr>
                </Fragment>;
          })}
          </tbody>

          {/* ── FOOTER: Portfolio averages ── */}
          <tfoot className="sticky bottom-0 z-10 bg-gray-100 border-t-2 border-gray-300" data-id="element-2541">
            <tr data-id="element-2542">
              <td className="sticky left-0 z-20 bg-gray-100 p-0 border-r border-gray-200" data-id="element-2543">
                <div className="px-3 py-3 overflow-hidden" style={{
                width: firstColWidth
              }} data-id="element-2544">
                  <span className="text-xs font-bold text-gray-700 uppercase tracking-wider" data-id="element-2545">
                    Portfolio Avg
                  </span>
                </div>
              </td>
              {/* Score average */}
              <td className="px-3 py-3 text-center border-r border-gray-200" data-id="element-2546">
                {(() => {
                const avgScore = filteredAndSortedProjects.length > 0 ? Math.round(filteredAndSortedProjects.reduce((s, p) => s + p.projectScore, 0) / filteredAndSortedProjects.length) : 0;
                const avgBand = getRiskBand(avgScore);
                const avgConfig = RISK_LEVEL_CONFIG[scoreToRiskLevel(avgScore)];
                return <span className={`inline-flex items-center justify-center min-w-[72px] h-9 px-3 rounded-lg text-sm font-black tracking-wide shadow-sm ${avgConfig.bgColor} ${avgConfig.color} border border-black/5`} data-id="element-2547">
                      {avgBand.short}
                    </span>;
              })()}
              </td>
              {tunedCategorySplits.map(({
              cat,
              liveParams,
              pendingParams
            }) => {
              const isExpanded = expandedCategories.has(cat.id);
              if (isExpanded) {
                return <Fragment key={cat.id} data-id="element-2548">
                      {liveParams.map((param, idx) => {
                    // Calculate portfolio average for this param
                    let totalWeight = 0;
                    let weightedSeveritySum = 0;
                    let dataCount = 0;
                    filteredAndSortedProjects.forEach(p => {
                      const {
                        severity,
                        isNoData
                      } = getProjectParamSeverity(p, param);
                      if (!isNoData) {
                        dataCount++;
                        weightedSeveritySum += SEVERITY_ORDER[severity];
                        totalWeight++;
                      }
                    });
                    const avgVal = dataCount > 0 ? weightedSeveritySum / totalWeight : 0;
                    let avg: FactorSeverity = 'none';
                    if (avgVal >= 2.5) avg = 'high';else if (avgVal >= 1.5) avg = 'medium';else if (avgVal >= 0.5) avg = 'low';
                    const style = dataCount === 0 ? SEVERITY_CELL_STYLES.no_data : SEVERITY_CELL_STYLES[avg];
                    return <td key={param.id} className={`px-2 py-3 text-center ${idx === 0 ? CAT_LEFT_BORDER_THIN[cat.id] || 'border-l border-gray-200' : ''}`} data-id="element-2549">
                            <div className={`inline-flex items-center justify-center min-w-[44px] h-7 px-2 rounded-md text-xs font-bold border ${style.bg} ${style.text} ${style.border}`} data-id="element-2550">
                              {dataCount === 0 ? '▧' : SEVERITY_LABELS[avg]}
                            </div>
                          </td>;
                  })}
                      {pendingParams.length > 0 && <td className="px-1.5 py-3 text-center" data-id="element-2551">
                          <div className="inline-flex items-center justify-center min-w-[36px] h-7 px-1.5 rounded-md text-[10px] font-bold border border-dashed border-gray-300 bg-gray-200/50 text-gray-400" data-id="element-2552">
                            ▧
                          </div>
                        </td>}
                    </Fragment>;
              } else {
                return <td key={cat.id} className={`px-2 py-3 text-center ${CAT_LEFT_BORDER_THIN[cat.id] || 'border-l border-gray-200'}`} data-id="element-2553">
                      {(() => {
                    const dp = cat.params.filter(p => p.dataAvailable && p.mapFromFactor);
                    if (dp.length === 0) {
                      const style = SEVERITY_CELL_STYLES.no_data;
                      return <div className={`inline-flex items-center justify-center min-w-[44px] h-7 px-2 rounded-md text-xs font-bold border ${style.bg} ${style.text} ${style.border}`} data-id="element-2554">
                              ▧
                            </div>;
                    }
                    let totalWorstSum = 0;
                    let dataCount = 0;
                    filteredAndSortedProjects.forEach(p => {
                      const {
                        worst,
                        allNoData
                      } = getProjectWorstSeverity(p, dp);
                      if (!allNoData) {
                        dataCount++;
                        totalWorstSum += SEVERITY_ORDER[worst];
                      }
                    });
                    const avgVal = dataCount > 0 ? totalWorstSum / dataCount : 0;
                    let avg: FactorSeverity = 'none';
                    if (avgVal >= 2.5) avg = 'high';else if (avgVal >= 1.5) avg = 'medium';else if (avgVal >= 0.5) avg = 'low';
                    const style = dataCount === 0 ? SEVERITY_CELL_STYLES.no_data : SEVERITY_CELL_STYLES[avg];
                    return <div className={`inline-flex items-center justify-center min-w-[44px] h-7 px-2 rounded-md text-xs font-bold border ${style.bg} ${style.text} ${style.border}`} data-id="element-2555">
                            {dataCount === 0 ? '▧' : SEVERITY_LABELS[avg]}
                          </div>;
                  })()}
                    </td>;
              }
            })}
            </tr>
          </tfoot>
        </table>
      </div>

      <Toast message={toastMessage} isVisible={showToast} onClose={() => setShowToast(false)} data-id="element-2556" />

      <RiskTuningPanel isOpen={showTuning} onClose={() => setShowTuning(false)} categoryWeights={tuningPanelWeights} onWeightsChange={handleWeightsChange} onOpenCustomization={() => setShowCustomization(true)} data-id="element-2557" />

      <ModelCustomizationModal isOpen={showCustomization} onClose={() => setShowCustomization(false)} initialCategories={categories} defaultCategories={DEFAULT_CATEGORIES} onSave={newCategories => {
      setCategories(newCategories);
      setToastMessage('Risk model updated successfully');
      setShowToast(true);
    }} data-id="element-2558" />

      {/* Project Detail Modal */}
      <AnimatePresence data-id="element-2559">
        {modalState && (() => {
        const project = filteredAndSortedProjects.find(p => p.projectName === modalState.projectName);
        if (!project) return null;
        const band = getRiskBand(project.projectScore);
        const riskConfig = RISK_LEVEL_CONFIG[scoreToRiskLevel(project.projectScore)];
        // Compute all param severities across all categories
        const allParamSeverities = categories.flatMap(cat => cat.params.filter(p => p.dataAvailable && p.mapFromFactor).map(param => {
          const {
            severity,
            isNoData,
            hasWorstCase
          } = getProjectParamSeverity(project, param);
          return {
            param,
            severity,
            isNoData,
            hasWorstCase,
            category: cat
          };
        })).filter(x => !x.isNoData && x.severity !== 'none').sort((a, b) => SEVERITY_ORDER[b.severity] - SEVERITY_ORDER[a.severity]);
        const topContributors = allParamSeverities.slice(0, 5);
        const hasMore = allParamSeverities.length > 5;
        // Pull project-level recommendations from the shared mock data
        const projectRecommendations = MOCK_RECOMMENDATIONS.filter(rec => rec.scope === 'project' && rec.affectedProjects.includes(project.projectName));
        return <>
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6" data-id="element-2560">
                  <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} onClick={() => setModalState(null)} className="absolute inset-0 bg-black/40 backdrop-blur-sm" data-id="element-2561" />
                  <motion.div initial={{
              opacity: 0,
              scale: 0.95,
              y: 20
            }} animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.95,
              y: 20
            }} transition={{
              type: 'spring',
              damping: 25,
              stiffness: 300
            }} className="relative w-full max-w-5xl bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-2562">
                    {/* Header */}
                    <div className="flex flex-col border-b border-gray-100 flex-shrink-0" data-id="element-2563">
                      <div className="flex items-center justify-between px-6 py-4" data-id="element-2564">
                        <div className="flex items-center gap-3" data-id="element-2565">
                          <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center" data-id="element-2566">
                            <Building2 className="text-indigo-600" size={20} data-id="element-2567" />
                          </div>
                          <div data-id="element-2568">
                            <h2 className="text-lg font-bold text-gray-900" data-id="element-2569">
                              {project.projectName}
                            </h2>
                            <div className="flex items-center gap-2 mt-0.5" data-id="element-2570">
                              <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded" data-id="element-2571">
                                {project.regionName}
                              </span>
                              <span className="text-xs text-gray-400" data-id="element-2572">
                                {project.activities.length} active
                                subcontractors
                              </span>
                              {project.sifPotential && <span className="text-xs font-bold text-red-700 bg-red-50 px-2 py-0.5 rounded border border-red-200" data-id="element-2573">
                                  ⚠ SIF Potential
                                </span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5" data-id="element-2574">
                          {/* Export Button */}
                          <div className="relative" ref={modalExportRef} data-id="element-2575">
                            <button onClick={() => setShowModalExport(v => !v)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors" data-id="element-2576">
                              <Download size={14} data-id="element-2577" />
                              Export
                              <ChevronDown size={10} data-id="element-2578" />
                            </button>
                            <AnimatePresence data-id="element-2579">
                              {showModalExport && <motion.div initial={{
                          opacity: 0,
                          y: -4
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} exit={{
                          opacity: 0,
                          y: -4
                        }} transition={{
                          duration: 0.12
                        }} className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1" data-id="element-2580">
                                  <button onClick={() => {
                            const allParams = categories.flatMap(c => c.params);
                            const headers = ['Parameter', 'Category', 'Weight', 'Severity', 'Explanation'];
                            const rows = allParams.filter(p => p.dataAvailable && p.mapFromFactor).map(param => {
                              const cat = categories.find(c => c.params.includes(param));
                              const {
                                severity,
                                isNoData
                              } = getProjectParamSeverity(project, param);
                              const reason = getParamScoreReason(param.id, severity, isNoData);
                              return [param.label, cat?.label || '', `${param.weight}%`, isNoData ? 'No Data' : severity, `"${reason.replace(/"/g, '""')}"`];
                            });
                            const csvContent = [`Project: ${project.projectName}`, `Business Unit: ${project.regionName}`, `Risk Score: ${project.projectScore}`, `Active Subcontractors: ${project.activities.length}`, '', headers.join(','), ...rows.map(r => r.join(','))].join('\n');
                            triggerDownload(csvContent, `${project.projectName.replace(/\s+/g, '-')}-risk-report.csv`, 'text/csv;charset=utf-8;');
                            setToastMessage(`Risk report exported for ${project.projectName}`);
                            setShowToast(true);
                            setShowModalExport(false);
                          }} className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-2581">
                                    <Download size={12} className="text-gray-400" data-id="element-2582" />
                                    Download CSV Report
                                  </button>
                                  <button onClick={() => {
                            window.print();
                            setShowModalExport(false);
                          }} className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-2583">
                                    <ExternalLink size={12} className="text-gray-400" data-id="element-2584" />
                                    Print Report
                                  </button>
                                </motion.div>}
                            </AnimatePresence>
                          </div>
                          <button onClick={() => setModalState(null)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-2585">
                            <X size={20} data-id="element-2586" />
                          </button>
                        </div>
                      </div>
                      {/* Tabs */}
                      <div className="flex items-center px-6 gap-6" data-id="element-2587">
                        <button onClick={() => setModalState({
                    ...modalState,
                    tab: 'quick'
                  })} className={`pb-3 text-sm font-bold border-b-2 transition-colors ${modalState.tab === 'quick' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-2588">
                          Risk Drivers & Actions
                        </button>
                        <button onClick={() => setModalState({
                    ...modalState,
                    tab: 'detailed'
                  })} className={`pb-3 text-sm font-bold border-b-2 transition-colors ${modalState.tab === 'detailed' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-2589">
                          Detailed
                        </button>
                      </div>
                    </div>

                    {/* Score Hero Bar */}
                    <div className="bg-gray-50 border-b border-gray-100 px-6 py-4 flex items-center gap-4 flex-shrink-0" data-id="element-2590">
                      <div className={`inline-flex items-center justify-center min-w-[80px] h-10 px-4 rounded-lg text-base font-black tracking-wide shadow-sm ${riskConfig.bgColor} ${riskConfig.color} border border-black/5`} data-id="element-2591">
                        {band.short}
                      </div>
                      <div data-id="element-2592">
                        <div className="text-sm font-bold text-gray-900" data-id="element-2593">
                          Project Risk Score: {project.projectScore}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-2594">
                          {band.label} Risk Level
                        </div>
                      </div>
                    </div>

                    {/* Scrollable Body */}
                    <div className="p-6 overflow-y-auto space-y-8" data-id="element-2595">
                      {modalState.tab === 'quick' ? <>
                          {/* Top Risk Contributors */}
                          <div data-id="element-2596">
                            <div className="flex items-center gap-2 mb-4" data-id="element-2597">
                              <AlertTriangle size={18} className="text-orange-500" data-id="element-2598" />
                              <h3 className="text-base font-bold text-gray-900 uppercase tracking-wider" data-id="element-2599">
                                Insights & Corrective Actions
                              </h3>
                            </div>
                            {projectRecommendations.length === 0 ? <p className="text-sm text-gray-400 italic" data-id="element-2600">
                                No significant risk contributors detected.
                              </p> : <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" data-id="element-2601">
                                <table className="w-full text-left text-sm" data-id="element-2602">
                                  <thead className="bg-gray-50 border-b border-gray-200" data-id="element-2603">
                                    <tr data-id="element-2604">
                                      <th className="px-4 py-3 font-bold text-gray-900 w-1/3" data-id="element-2605">
                                        Insight
                                      </th>
                                      <th className="px-4 py-3 font-bold text-gray-900 w-1/3" data-id="element-2606">
                                        Corrective Action
                                      </th>
                                      <th className="px-4 py-3 font-bold text-gray-900 text-center w-28" data-id="element-2607">
                                        Risk Reduction
                                      </th>
                                      <th className="px-4 py-3 font-bold text-gray-900 w-40" data-id="element-2608">
                                        Risk Driver
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100" data-id="element-2609">
                                    {projectRecommendations.slice(0, 3).map((rec, idx) => {
                            const paramLabel = rec.linkedDrivers[0];
                            const paramInfo = allParamSeverities.find(p => p.param.label === paramLabel);
                            const severity = paramInfo?.severity || 'medium';
                            const category = paramInfo?.category || categories[0];
                            const style = SEVERITY_CELL_STYLES[severity];
                            return <tr key={rec.id} className="hover:bg-gray-50 transition-colors" data-id="element-2610">
                                            {/* Insight */}
                                            <td className="px-4 py-4 align-top" data-id="element-2611">
                                              <div className="text-xs text-gray-600 leading-relaxed" data-id="element-2612">
                                                {rec.rationale}
                                              </div>
                                            </td>
                                            {/* Recommendation */}
                                            <td className="px-4 py-4 align-top" data-id="element-2613">
                                              <div className="font-bold text-gray-900 text-sm mb-2" data-id="element-2614">
                                                {rec.title}
                                              </div>
                                              <button onClick={e => {
                                  e.stopPropagation();
                                  setSelectedModalRecommendation(rec);
                                }} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-gray-900 bg-amber-400 hover:bg-amber-500 px-2.5 py-1 rounded-md shadow-sm transition-colors" data-id="element-2615">
                                                <Sparkles size={11} data-id="element-2616" />
                                                Discuss with Bilti
                                              </button>
                                            </td>
                                            {/* Risk Reduction */}
                                            <td className="px-4 py-4 align-top text-center" data-id="element-2617">
                                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-orange-50 text-orange-700 border border-orange-200 whitespace-nowrap" data-id="element-2618">
                                                <TrendingDown size={12} data-id="element-2619" />-
                                                {rec.riskReduction}% Risk
                                              </span>
                                            </td>
                                            {/* Risk Driver */}
                                            <td className="px-4 py-4 align-top" data-id="element-2620">
                                              <div className="flex items-center gap-1.5 mb-1.5" data-id="element-2621">
                                                <span className="font-bold text-gray-900 text-sm" data-id="element-2622">
                                                  {paramLabel}
                                                </span>
                                                {paramInfo?.param.aiEvaluated && <Sparkles size={12} className="text-purple-400" data-id="element-2623" />}
                                              </div>
                                              <div className="flex items-center gap-2" data-id="element-2624">
                                                <div className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-[10px] font-bold border ${style.bg} ${style.text} ${style.border}`} data-id="element-2625">
                                                  {SEVERITY_LABELS[severity]}
                                                </div>
                                                <span className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded" data-id="element-2626">
                                                  {category.label}
                                                </span>
                                              </div>
                                            </td>
                                          </tr>;
                          })}
                                  </tbody>
                                </table>
                              </div>}
                          </div>
                        </> : <>
                          {/* Detailed Tab */}
                          <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-5" data-id="element-2627">
                            <h3 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-2628">
                              Risk Score Explanation
                            </h3>
                            <p className="text-sm text-indigo-800 leading-relaxed" data-id="element-2629">
                              The project risk score of{' '}
                              <span className="font-bold" data-id="element-2630">
                                {project.projectScore}
                              </span>{' '}
                              is a weighted average across{' '}
                              {project.activities.length} active subcontractors.
                              Higher risk activities carry more weight in the
                              calculation. The score is currently elevated
                              primarily due to issues in{' '}
                              {topContributors[0]?.category.label || 'several categories'}
                              .
                            </p>
                          </div>

                          <div className="space-y-6" data-id="element-2631">
                            {categories.map(cat => {
                      const catParams = cat.params.filter(p => p.dataAvailable && p.mapFromFactor).map(param => {
                        const {
                          severity,
                          isNoData
                        } = getProjectParamSeverity(project, param);
                        return {
                          param,
                          severity,
                          isNoData
                        };
                      }).filter(x => !x.isNoData);
                      if (catParams.length === 0) return null;
                      const catWeight = getCategoryWeight(cat);
                      const {
                        worst,
                        allNoData
                      } = getProjectWorstSeverity(project, cat.params);
                      const isExpanded = modalExpandedCategories.has(
                      // Option to expand or collapse
                      cat.id);
                      const toggleModalCat = () => {
                        setModalExpandedCategories(prev => {
                          const next = new Set(prev);
                          if (next.has(cat.id)) next.delete(cat.id);else next.add(cat.id);
                          return next;
                        });
                      };
                      return <div key={cat.id} id={`modal-cat-${cat.id}`} className="rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm" data-id="element-2632">
                                  <button onClick={toggleModalCat} className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors border-b border-gray-200 flex items-center justify-between" data-id="element-2633">
                                    <div className="flex items-center gap-3" data-id="element-2634">
                                      <span className="text-lg" data-id="element-2635">
                                        {cat.icon}
                                      </span>
                                      <h4 className="text-sm font-bold text-gray-900" data-id="element-2636">
                                        {cat.label}
                                      </h4>
                                      <span className="text-xs font-semibold text-gray-500 bg-gray-200/50 px-2 py-0.5 rounded" data-id="element-2637">
                                        {catWeight}% Weight
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-3" data-id="element-2638">
                                      <div className={`inline-flex items-center justify-center min-w-[48px] h-7 px-2 rounded-md text-xs font-bold border ${allNoData ? SEVERITY_CELL_STYLES.no_data.bg : SEVERITY_CELL_STYLES[worst].bg} ${allNoData ? SEVERITY_CELL_STYLES.no_data.text : SEVERITY_CELL_STYLES[worst].text} ${allNoData ? SEVERITY_CELL_STYLES.no_data.border : SEVERITY_CELL_STYLES[worst].border}`} data-id="element-2639">
                                        {allNoData ? '▧' : worst === 'none' ? '—' : SEVERITY_LABELS[worst]}
                                      </div>
                                      {isExpanded ? <ChevronUp size={16} className="text-gray-400" data-id="element-2640" /> : <ChevronDown size={16} className="text-gray-400" data-id="element-2641" />}
                                    </div>
                                  </button>
                                  {isExpanded && <div className="divide-y divide-gray-100" data-id="element-2642">
                                      {catParams.map(({
                            param,
                            severity
                          }) => {
                            const style = severity === 'none' ? SEVERITY_CELL_STYLES.no_data : SEVERITY_CELL_STYLES[severity];
                            // modified block below
                            const isHighlighted = modalState.scrollToParamId === param.id;
                            const driverExp = getDriverExplanation(param.id, project.projectName);
                            const mockExplanation = driverExp ? driverExp.whyExplanation : severity === 'high' || severity === 'medium' ? `Flagged due to recent negative observations. Example: Missing documentation for ${param.label.toLowerCase()} found during site walk.` : `Performing well. Example: Consistent compliance with ${param.label.toLowerCase()} standards across all trades.`;
                            return <div key={param.id} id={`param-${param.id}`} className={`p-4 transition-colors ${isHighlighted ? 'bg-indigo-50/50' : ''}`} data-id="element-2643">
                                            <div className="flex items-start gap-4" data-id="element-2644">
                                              <div className={`inline-flex items-center justify-center min-w-[48px] h-7 px-2 rounded-md border ${style.bg} ${style.text} ${style.border} mt-0.5`} data-id="element-2645">
                                                {severity === 'none' ? '—' : SEVERITY_LABELS[severity]}
                                              </div>
                                              <div data-id="element-2646">
                                                <div className="flex items-center gap-2 mb-1" data-id="element-2647">
                                                  <span className="text-sm font-bold text-gray-900" data-id="element-2648">
                                                    {param.label}
                                                  </span>
                                                  {param.aiEvaluated && <Sparkles size={12} className="text-purple-400" data-id="element-2649" />}
                                                </div>
                                                <p className="text-sm text-gray-600 mb-2" data-id="element-2650">
                                                  {param.tooltip}
                                                </p>
                                                <div className="bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-2651">
                                                  <p className="text-sm text-gray-700" data-id="element-2652">
                                                    <span className="font-semibold text-gray-900" data-id="element-2653">
                                                      Observation:{' '}
                                                    </span>
                                                    {mockExplanation}
                                                  </p>
                                                </div>
                                              </div>
                                            </div>
                                          </div>;
                          })}
                                    </div>}
                                </div>;
                    })}
                          </div>
                        </>}
                    </div>
                  </motion.div>
                </div>
              </>;
      })()}
      </AnimatePresence>

      {/* Recommendation Detail Modal (Discuss with Bilti) */}
      <RecommendationDetailModal recommendation={selectedModalRecommendation} onClose={() => setSelectedModalRecommendation(null)} data-id="element-2654" />
    </div>;
}